// eslint-disable-next-line react-hooks/react-compiler -- pre-ratchet baseline (BLOOP-3033)
/* eslint-disable react-hooks/rules-of-hooks */

import * as React from 'react';
import TextInput from '@packages/systems/design-system/TextInput';
import TextArea from '@packages/systems/design-system/Textarea';
import Hitarea from '@packages/systems/design-system/Hitarea';
import {
  getMeta,
  getSystem,
  Result,
  TypeImplementation,
  Types,
  type Type,
} from '@packages/systems/wfdl';

import {
  type ContextualExpression,
  ContextualExpressions,
  stripAnn,
  getAnn,
} from '@packages/utilities/contextual-expression';
import {
  updateExpressionAction,
  useExpressionEditorImplementationContext,
} from '@packages/systems/ExpressionEditor-utils';
import {EBoolean, EText, EEnum} from '@packages/systems/wfdl-ast';

import {iconName, pluginName, qualify} from '@packages/systems/wfdl-Qualified';
import {emptyObject, emptyArray} from '@packages/utilities/fp/utils';
import {useTheme} from '@packages/utilities/styled';
import TooltipTrigger from '@packages/systems/design-system/TooltipTrigger';
import {NumberPrimitiveType} from './NumberPrimitiveType';
import {ElementPrimitiveType} from './ElementPrimitiveType';
import {
  Group,
  Select,
  Option,
} from '@packages/systems/design-system/WebflowSelect';
import {flushSync} from 'react-dom';
import {
  Checkbox,
  SegmentedControl,
  SegmentedControlButton,
} from '@packages/systems/spring';

// Keep for reference:

// type EnumDataType = {|
//   type: 'Enum',
//   // Enum meta information.
//   meta?: {|
//       // UI variant to display – defaults to segmented buttons, `menu` renders a dropdown select menu
//     variant?: 'menu',

//     /**
//      * Do not preview the change with the transaction system before selecting an
//      * option. Defaults to `false`
//      */
//     withoutLivePreview?: boolean,

//     // Placeholder for menu if no item is selected
//     placeholder?: string,
//   |},
//   // The map of available options, the key being the value of the option that will be stored.
//   values: {
//     [string]: {
//       // Primary UI description for this specific option.
//       label: string,
//     },
//   },
//   system?: DataTypeSystem<string>,
// |};
type EnumRenderProps = {
  state: string;
  onChange: (arg1: string) => void;
  dataType: Type;
};

const EnumSegmentedButtons = ({state, onChange, dataType}: EnumRenderProps) => {
  const values = Types.getEnumValues(dataType) || emptyObject;
  const meta = getMeta(dataType) || defaultEnumMeta;

  return (
    <SegmentedControl
      data-automation-id={`${Types.generateAutomationIds(dataType)}`}
      value={state}
      onChange={(value) => {
        onChange(value);
      }}
    >
      {Object.keys(values).map((value) => {
        const labelOverride = meta.optionLabels
          ? meta.optionLabels[value]
          : null;
        const label = labelOverride || values[value] || value;

        return (
          <SegmentedControlButton
            data-automation-id={`visual-radio-button-${value}`}
            aria-label={label}
            key={value}
            value={value}
            truncationProps={
              meta.disableTruncation ? undefined : {truncateToLines: 1}
            }
          >
            {label}
          </SegmentedControlButton>
        );
      })}
    </SegmentedControl>
  );
};

const defaultEnumMeta: Record<string, any> = {};

const EnumDropdownMenu = ({state, onChange, dataType}: EnumRenderProps) => {
  const {env} = useExpressionEditorImplementationContext();
  // Force `dropdownState.value` to always be in sync with `state`; this
  // prevents the selected value from appearing incorrect when `state` changes
  // without interacting with the dropdown (e.g. via `UNDO`):
  const meta = getMeta(dataType) || defaultEnumMeta;

  const elementId = Types.generateAutomationIds(dataType);
  const values = Types.getEnumValues(dataType) || emptyObject;
  return (
    <Hitarea>
      <Select
        id={elementId}
        data-automation-id={elementId}
        value={state}
        disabled={Object.keys(values).length === 0}
        placeholder={meta.placeholder}
        onChange={(value, {changeType}) => {
          // Opt out of transactional updates
          if (meta.withoutLivePreview) {
            // Dispatch value only when change is committed.
            if (changeType === 'changeEnd') {
              onChange(value);
            }
            return;
          }

          if (changeType === 'changeStart') {
            env.transaction.begin();
          } else if (changeType === 'changeEnd') {
            env.transaction.commit();
          } else if (changeType === 'changeCancel') {
            env.transaction.abort();
          } else {
            onChange(value);
          }
        }}
      >
        <Group>
          {Object.keys(values).map((value) => {
            const labelOverride = meta.optionLabels
              ? meta.optionLabels[value]
              : null;
            return (
              <Option
                key={value}
                value={value}
                data-automation-id={`${elementId}-option-${value}`}
                aria-describedby={`${elementId}-hover-note-${value}`}
                label={
                  <TooltipTrigger alternate content={value}>
                    <span>{labelOverride || values[value] || value}</span>
                  </TooltipTrigger>
                }
              />
            );
          })}
        </Group>
      </Select>
    </Hitarea>
  );
};

const defaultSystem: Record<string, any> = {};
const defaultTextMeta: Record<string, any> = {};

export const PrimitiveTypes: {
  Boolean: TypeImplementation.TypeImplementation;
  Text: TypeImplementation.TypeImplementation;
  Number: TypeImplementation.TypeImplementation;
  Element: TypeImplementation.TypeImplementation;
  Enum: TypeImplementation.TypeImplementation;
} = {
  Boolean: TypeImplementation.Custom({
    parseValue: (_) =>
      Result.Error('Cannot use Literal expressions to create Boolean values.'),
    render: ({expr, dispatch}) => {
      const type = getAnn(expr)?.type;
      const maybeSystem = type ? getSystem(type) : null;
      const system = maybeSystem ? maybeSystem : defaultSystem;
      const isChecked =
        ContextualExpressions.getBoolean(stripAnn(expr)) || false;

      return (
        <Checkbox
          aria-label={system.label || 'Yes'}
          isChecked={isChecked}
          data-automation-id={`${
            type ? Types.generateAutomationIds(type) : 'boolean-primitive'
          }`}
          onChange={() => {
            dispatch(updateExpressionAction(EBoolean(!isChecked)));
          }}
        />
      );
    },
    renderSummary: ({expr}: {expr: ContextualExpression}) => (
      <span>{String(ContextualExpressions.getBoolean(stripAnn(expr)))}</span>
    ),
    icon: TypeImplementation.IconByName(
      qualify(pluginName('Basic'), iconName('Boolean'))
    ),
    system: {defaultValue: false, label: 'Switch'},
  }),
  Text: TypeImplementation.Custom({
    parseValue: (_) =>
      Result.Error('Cannot use Literal expressions to create Text values.'),
    render: ({expr, dispatch}) => {
      const {env} = useExpressionEditorImplementationContext();
      const type = getAnn(expr)?.type;
      const maybeMeta = type ? getMeta(type) : null;
      const meta = maybeMeta ? maybeMeta : defaultTextMeta;
      const [invalid, setInvalid] = React.useState(false);
      const text = ContextualExpressions.getText(stripAnn(expr));
      const [value, setValue] = React.useState(text !== null ? text : '');

      React.useLayoutEffect(() => {
        const newText = ContextualExpressions.getText(stripAnn(expr));
        setValue(newText || '');
        setInvalid(false);
      }, [expr]);

      const onFocus = React.useCallback(() => {
        env.transaction.begin();
        setInvalid(false);
      }, [env.transaction]);

      const onBlur = React.useCallback(() => {
        if (meta.validation && meta.validation.regex) {
          const validate = new RegExp(meta.validation.regex);

          if (!validate.test(value)) {
            setInvalid(true);
            return;
          }
        }

        env.transaction.commit();
      }, [meta.validation, env.transaction, value]);

      const onChange = React.useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
          flushSync(() => {
            setValue(e.target.value);
          });
          dispatch(updateExpressionAction(EText(e.target.value)));
        },
        [dispatch]
      );

      const theme = useTheme();

      if (meta.multiLine) {
        const placeholderStyles = {
          padding: theme.gutters.base,
        } as const;
        const style = {
          minHeight: theme.gutters.base * 9,
          padding: theme.gutters.base,
          zIndex: undefined,
        } as const;
        return (
          <TextArea
            placeholderStyles={placeholderStyles}
            data-automation-id={`${
              type ? Types.generateAutomationIds(type) : 'text-primitive'
            }`}
            style={style}
            onFocus={onFocus}
            onBlur={onBlur}
            onChange={onChange}
            placeholderValue={meta.placeholder}
            text={value}
            rows={5}
            selectAll
          />
        );
      }

      return (
        <Hitarea>
          <TextInput
            type="text"
            data-automation-id={`${
              type ? Types.generateAutomationIds(type) : 'text-primitive'
            }`}
            placeholder={meta.placeholder}
            value={value}
            onFocus={onFocus}
            onBlur={onBlur}
            onChange={onChange}
            invalid={invalid}
          />
        </Hitarea>
      );
    },
    renderSummary: ({expr}: {expr: ContextualExpression}) => (
      <span>{ContextualExpressions.getText(stripAnn(expr))}</span>
    ),
    icon: TypeImplementation.IconByName(
      qualify(pluginName('Basic'), iconName('PlainText'))
    ),
    system: {
      defaultValue: '',
      // displayName drives the connect menu ("New Plain Text property"),
      // default prop name, and section header ("Default Plain Text").
      // label is the shorter form shown next to the text input in property
      // settings, where horizontal space is limited.
      displayName: 'Plain Text',
      label: 'Text',
    },
  }),
  Number: NumberPrimitiveType,
  Element: ElementPrimitiveType,
  Enum: TypeImplementation.Custom({
    params: ['Options'],
    parseValue: (_) =>
      Result.Error('Cannot use Literal expressions to create Enum values.'),
    render: ({expr, dispatch}) => {
      const type = getAnn(expr)?.type;
      const maybeMeta = type ? getMeta(type) : null;
      const meta = maybeMeta ? maybeMeta : defaultEnumMeta;

      const keys = type
        ? Object.keys(Types.getEnumValues(type) || emptyObject)
        : emptyArray;
      // HACK — Animation atom's top level is a Union type, but since we only
      // have one option (with future plans to add more) we hide the menu for
      // now so as to not confuse users.
      if (keys.includes('lottie')) {
        if (keys.length === 1) {
          return null;
        } else {
          throw new Error(
            'Yay! We‘re adding more Animation types — don‘t forget to update this hack here.'
          );
        }
      }

      const state = ContextualExpressions.getEnum(stripAnn(expr));
      if (!type) {
        return null;
      }
      const enumDispatch = (value: string) => {
        dispatch(updateExpressionAction(EEnum(value)));
      };
      switch (meta.variant) {
        case 'menu':
          return (
            <EnumDropdownMenu
              state={state ? state : ''}
              onChange={enumDispatch}
              dataType={type}
            />
          );
        default:
          return (
            <EnumSegmentedButtons
              state={state ? state : ''}
              onChange={enumDispatch}
              dataType={type}
            />
          );
      }
    },
    renderSummary: ({expr}: {expr: ContextualExpression}) => (
      <span>{ContextualExpressions.getEnum(stripAnn(expr))}</span>
    ),
    icon: TypeImplementation.IconByName(
      qualify(pluginName('Basic'), iconName('Enum'))
    ),
    system: {defaultValue: '', label: 'Choice'},
  }),
};
